import type { BlockPos } from './core/BlockPos.js'

export const MAX_PICKING_VOLUME = 0xffffffff

export function getPickingVolume(size: BlockPos): number {
	return size[0] * size[1] * size[2]
}

export function assertPickableStructureSize(size: BlockPos): void {
	const volume = getPickingVolume(size)
	if (!Number.isSafeInteger(volume) || volume < 0 || volume > MAX_PICKING_VOLUME) {
		throw new Error(`Structure volume ${volume} exceeds GPU picking limit ${MAX_PICKING_VOLUME}`)
	}
}

export function encodeBlockPosToPickId(pos: BlockPos, size: BlockPos): number {
	assertPickableStructureSize(size)
	if (
		pos[0] < 0 || pos[0] >= size[0] ||
		pos[1] < 0 || pos[1] >= size[1] ||
		pos[2] < 0 || pos[2] >= size[2]
	) {
		throw new Error(`Cannot encode block position ${pos.join(',')} outside structure size ${size.join(',')}`)
	}
	return pos[0] * size[1] * size[2] + pos[1] * size[2] + pos[2] + 1
}

export function decodePickIdToBlockPos(id: number, size: BlockPos): BlockPos | null {
	assertPickableStructureSize(size)
	if (id === 0) {
		return null
	}
	if (!Number.isSafeInteger(id) || id < 0 || id > getPickingVolume(size)) {
		return null
	}
	const index = id - 1
	const yz = size[1] * size[2]
	const x = Math.floor(index / yz)
	const rem = index - x * yz
	const y = Math.floor(rem / size[2])
	const z = rem - y * size[2]
	return [x, y, z]
}

export function packPickIdToRgba(id: number): [number, number, number, number] {
	if (!Number.isSafeInteger(id) || id < 0 || id > MAX_PICKING_VOLUME) {
		throw new Error(`Cannot pack GPU picking id ${id}`)
	}
	return [
		id % 256,
		Math.floor(id / 256) % 256,
		Math.floor(id / 65536) % 256,
		Math.floor(id / 16777216) % 256,
	]
}

export function unpackPickIdFromRgba(rgba: ArrayLike<number>): number {
	return rgba[0] +
		rgba[1] * 256 +
		rgba[2] * 65536 +
		rgba[3] * 16777216
}
